import { useSEO } from "@/hooks/useSEO";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import { useCurrency } from "@/context/CurrencyContext";
import { motion } from "framer-motion";
import { Users, Luggage, Fuel, MapPin, ArrowLeft, ArrowRight, ShieldCheck, Cog, Percent } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1601315639340-ce7dd5bf78f5?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function VehicleDetail() {
  const { vehicleId } = useParams();
  const [v, setV] = useState(null);
  const [unavail, setUnavail] = useState({ blackouts: [], booked: [] });
  const [notFound, setNotFound] = useState(false);
  const { currency, convert } = useCurrency();

  useEffect(() => {
    api.get(`/vehicles/${vehicleId}`).then((r) => setV(r.data)).catch(() => setNotFound(true));
    api.get(`/vehicles/${vehicleId}/unavailability`).then((r) => setUnavail(r.data)).catch(() => {});
  }, [vehicleId]);

  useSEO({
    title: v ? `${v.name} — ${fmtMoney(v.price_per_day)}/day` : "Vehicle",
    description: v ? `Rent the ${v.name} in Nassau, Bahamas — ${v.seats} seats, fits ${v.luggage ?? 0} bags, ${fmtMoney(v.price_per_day)}/day with airport drop-off available.` : "Rental vehicle in Nassau, Bahamas",
    path: `/fleet/${vehicleId}`,
    jsonLd: v ? {
      "@context": "https://schema.org",
      "@type": "Product",
      name: `${v.name} — rental in Nassau, Bahamas`,
      image: v.image_url ? (v.image_url.startsWith("http") ? v.image_url : `https://easydrivecarrental.com${v.image_url}`) : undefined,
      description: `Rent the ${v.name} in Nassau — ${v.seats} seats, fits ${v.luggage ?? 0} bags, unlimited mileage on New Providence.`,
      brand: { "@type": "Brand", name: "Easy Drive Car Rental" },
      offers: {
        "@type": "Offer",
        price: v.price_per_day,
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        url: `https://easydrivecarrental.com/fleet/${v.id}`,
      },
    } : null,
  });

  if (notFound)
    return (
      <div data-testid="vehicle-detail-notfound" className="pt-40 pb-24 text-center">
        <p className="text-muted-foreground font-ui">This vehicle is no longer in the fleet.</p>
        <Link to="/fleet" className="gold-text font-ui hover:underline">Browse the fleet →</Link>
      </div>
    );
  if (!v) return <div className="pt-40 pb-24 text-center text-muted-foreground font-ui">Loading…</div>;

  const weekly = v.price_per_day * 7 * 0.9;

  return (
    <div data-testid="vehicle-detail-page" className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20">
      <Link data-testid="vehicle-detail-back" to="/fleet" className="inline-flex items-center gap-2 text-sm font-ui text-muted-foreground hover:text-primary transition-colors duration-200">
        <ArrowLeft size={15} /> Back to fleet
      </Link>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-10">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-3">
          <div className="relative rounded-3xl overflow-hidden shadow-[0_24px_60px_rgba(26,10,12,0.12)]">
            <img src={v.image_url || FALLBACK} alt={v.name} onError={(e) => (e.currentTarget.src = FALLBACK)} className="w-full aspect-[16/10] object-cover" />
            <span className="absolute top-5 left-5 text-[10px] font-ui tracking-[0.2em] uppercase bg-black/60 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 text-white">{v.category}</span>
            {v.lot_status && (
              <span data-testid="vehicle-detail-lot-status" className={`absolute top-5 right-5 text-[10px] font-ui tracking-[0.15em] uppercase backdrop-blur-md border rounded-full px-4 py-2 ${v.lot_status === "on_lot" ? "bg-emerald-500/85 border-emerald-300/40 text-white" : v.lot_status === "rented" ? "bg-amber-500/85 border-amber-300/40 text-white" : "bg-black/60 border-white/10 text-white/80"}`}>
                {v.lot_status === "on_lot" ? "On lot today" : v.lot_status === "rented" ? "Currently rented" : "Off lot"}
              </span>
            )}
          </div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { icon: Users, label: `${v.seats} seats` },
              { icon: Luggage, label: `${v.luggage ?? 0} bags` },
              { icon: Cog, label: v.transmission || "Automatic" },
              { icon: Fuel, label: `Fuel: ${v.fuel_level || "Full"}` },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="rounded-2xl border border-border bg-white px-4 py-3.5 flex items-center gap-2.5 text-sm font-ui">
                <Icon size={16} className="text-primary shrink-0" /> {label}
              </div>
            ))}
          </div>
          {v.features?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {v.features.map((f) => (
                <span key={f} className="text-sm sm:text-xs font-ui border border-border rounded-full px-3.5 py-1.5 text-muted-foreground bg-white">{f}</span>
              ))}
            </div>
          )}
          {v.location_label && (
            <p data-testid="vehicle-detail-location" className="mt-4 flex items-center gap-1.5 text-sm font-ui text-emerald-700">
              <MapPin size={14} /> {v.location_label}{v.location_note ? ` — ${v.location_note}` : ""}
            </p>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="lg:col-span-2 space-y-6">
          <div>
            <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Easy Drive Fleet</p>
            <h1 data-testid="vehicle-detail-name" className="mt-2 font-display text-4xl sm:text-5xl tracking-tight">{v.name}</h1>
          </div>
          <div className="rounded-3xl border border-border bg-white p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <div className="flex items-baseline justify-between">
              <p data-testid="vehicle-detail-price" className="gold-text font-ui font-semibold text-3xl">{fmtMoney(v.price_per_day)} <span className="text-xs text-muted-foreground font-normal">USD / day</span></p>
              {currency !== "USD" && <p data-testid="vehicle-detail-price-converted" className="text-sm font-ui text-muted-foreground">≈ {convert(v.price_per_day)}</p>}
            </div>
            <div className="mt-4 rounded-2xl bg-primary/5 border border-primary/20 px-4 py-3 flex items-center gap-3">
              <Percent size={16} className="text-primary shrink-0" />
              <p className="text-sm font-ui"><span className="font-semibold">7+ days: {fmtMoney(weekly)}</span> <span className="text-muted-foreground">— 10% weekly discount included</span></p>
            </div>
            <Link to={`/book/${v.id}`} className="block mt-6">
              <Button data-testid="vehicle-detail-book-button" className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-12 text-base tracking-wide transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(199,30,46,0.35)]">
                Book This Vehicle <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <p className="mt-3 flex items-center gap-1.5 text-[11px] font-ui text-muted-foreground">
              <ShieldCheck size={12} className="text-emerald-600" /> Basic insurance & unlimited mileage on New Providence included
            </p>
          </div>
          <AvailabilityCalendar booked={unavail.booked} blackouts={unavail.blackouts} />
        </motion.div>
      </div>
    </div>
  );
}
